import React, { useState, useEffect } from 'react';
import axios from './api/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Typography,
  Paper,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Star,
  StarBorder,
  Settings,
  Language as LanguageIcon
} from '@mui/icons-material';

const TranslationConfigManager = ({ open, onClose, onConfigCreated, userPermissions }) => {
  const [configs, setConfigs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: 'Dutch',  // Por defecto holandés como idioma origen
    target_language: 'English',  // Siempre inglés
    context: '',
    domain: '',
    is_default: false,
    glossary_ids: []  // IDs de glosarios seleccionados
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para gestión de idiomas dinámicos
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [showAddLanguageDialog, setShowAddLanguageDialog] = useState(false);
  const [newLanguageForm, setNewLanguageForm] = useState({
    name: '',
    name_native: ''
  });
  
  // Estados para glosarios
  const [availableGlossaries, setAvailableGlossaries] = useState([]);
  const [loadingGlossaries, setLoadingGlossaries] = useState(false);

  useEffect(() => {
    if (open) {
      loadConfigs();
      loadLanguages();
      loadGlossaries();
    }
  }, [open]);

  const loadConfigs = async () => {
    try {
      const response = await axios.get('/translation-configs/');
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Error loading configs:', error);
      setError('Error al cargar configuraciones');
    }
  };

  const loadLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const response = await axios.get('terminology/languages/');
      // Asegurar que siempre tengamos un array
      const languages = Array.isArray(response.data) ? response.data : 
                       (response.data?.results ? response.data.results : []);
      setAvailableLanguages(languages);
    } catch (error) {
      console.error('Error loading languages:', error);
      setError('Error al cargar idiomas disponibles');
      setAvailableLanguages([]); // Establecer array vacío en caso de error
    } finally {
      setLoadingLanguages(false);
    }
  };

  const loadGlossaries = async () => {
    setLoadingGlossaries(true);
    try {
      const response = await axios.get('terminology/glossaries/?is_active=true');
      const glossaries = Array.isArray(response.data) ? response.data : 
                        (response.data?.results ? response.data.results : []);
      setAvailableGlossaries(glossaries);
    } catch (error) {
      console.error('Error loading glossaries:', error);
      setError('Error al cargar glosarios disponibles');
      setAvailableGlossaries([]);
    } finally {
      setLoadingGlossaries(false);
    }
  };

  const handleCreateLanguage = async () => {
    if (!newLanguageForm.name || !newLanguageForm.name_native) {
      setError('Todos los campos son obligatorios');
      return;
    }

    const code = newLanguageForm.name.substring(0, 2).toLowerCase();
    const dataToSend = {
      ...newLanguageForm,
      code: code
    };

    try {
      await axios.post('terminology/languages/', dataToSend);
      setSuccess('Idioma creado exitosamente');
      setShowAddLanguageDialog(false);
      setNewLanguageForm({ name: '', name_native: '' });
      loadLanguages();
    } catch (error) {
      const message = error.response?.data?.detail || 
                     error.response?.data?.code?.[0] || 
                     'Error al crear el idioma';
      setError(message);
      console.error('Error creating language:', error);
    }
  };

  const handleCreateConfig = async () => {
    try {
      console.log('[handleCreateConfig] Creating config:', formData);
      const response = await axios.post('/translation-configs/create/', formData);
      console.log('[handleCreateConfig] Creation successful:', response.data);
      setSuccess('Configuración creada exitosamente');
      setShowForm(false);
      resetForm();
      loadConfigs();
      if (onConfigCreated) {
        onConfigCreated(response.data.config);
      }
    } catch (error) {
      console.error('[handleCreateConfig] Error creating config:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al crear configuración';
      setError(errorMessage);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      console.log('[handleUpdateConfig] Updating config:', editingConfig.id, formData);
      const response = await axios.put(`/translation-configs/${editingConfig.id}/update/`, formData);
      console.log('[handleUpdateConfig] Update successful:', response.data);
      setSuccess('Configuración actualizada exitosamente');
      setShowForm(false);
      setEditingConfig(null);
      resetForm();
      loadConfigs();
      // Notificar al componente padre para que actualice su estado
      if (onConfigCreated) {
        onConfigCreated();
      }
    } catch (error) {
      console.error('[handleUpdateConfig] Error updating config:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al actualizar configuración';
      setError(errorMessage);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta configuración?')) {
      return;
    }
    
    try {
      console.log('[handleDeleteConfig] Deleting config:', configId);
      await axios.delete(`/translation-configs/${configId}/delete/`);
      setSuccess('Configuración eliminada exitosamente');
      loadConfigs();
      // Notificar al componente padre para que actualice su estado
      if (onConfigCreated) {
        onConfigCreated();
      }
    } catch (error) {
      console.error('[handleDeleteConfig] Error deleting config:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al eliminar configuración';
      setError(errorMessage);
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      target_language: config.target_language,
      context: config.context || '',
      domain: config.domain || '',
      is_default: config.is_default,
      glossary_ids: config.glossaries ? config.glossaries.map(g => g.id) : []
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: 'Dutch',  // Por defecto holandés como idioma origen
      target_language: 'English',  // Siempre inglés
      context: '',
      domain: '',
      is_default: false,
      glossary_ids: []
    });
    setEditingConfig(null);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingConfig(null);
    setError('');
    setSuccess('');
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Settings />
          <Typography variant="h6">Translation Configuration</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        
        {!showForm ? (
          <>
            {userPermissions?.can_create && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowForm(true)}
                sx={{ mb: 2 }}
                fullWidth
              >
                New Configuration
              </Button>
            )}
            
            <List>
              {configs.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  No configurations created
                </Typography>
              ) : (
                configs.map((config) => (
                  <Paper key={config.id} sx={{ mb: 2, p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {config.is_default ? (
                            <Star color="primary" fontSize="small" />
                          ) : (
                            <StarBorder fontSize="small" />
                          )}
                          <Typography variant="h6">{config.name} → {config.target_language}</Typography>
                          {config.is_default && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                          {config.created_by && (
                            <Chip label={`By ${config.created_by}`} size="small" variant="outlined" color="default" />
                          )}
                        </Box>
                        
                        <Box mt={1}>
                          <Chip label={`Source: ${config.name}`} size="small" sx={{ mr: 1 }} color="info" />
                          <Chip label={`Target: ${config.target_language}`} size="small" sx={{ mr: 1 }} color="success" />
                          {config.domain && <Chip label={`Domain: ${config.domain}`} size="small" />}
                        </Box>
                        
                        {config.context && (
                          <Typography variant="caption" display="block" mt={1} color="text.secondary">
                            <strong>Context:</strong> {config.context}
                          </Typography>
                        )}
                        
                        {config.glossaries && config.glossaries.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              <strong>Active Glossaries:</strong>
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {config.glossaries.map(glossary => (
                                <Chip 
                                  key={glossary.id}
                                  label={glossary.name} 
                                  size="small" 
                                  variant="outlined"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                      
                      <Box>
                        {userPermissions?.can_edit && (
                          <IconButton onClick={() => handleEditConfig(config)} size="small">
                            <Edit />
                          </IconButton>
                        )}
                        {userPermissions?.can_delete && (
                          <IconButton onClick={() => handleDeleteConfig(config.id)} size="small" color="error">
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </List>
          </>
        ) : (
          <Box component="form" sx={{ mt: 2 }}>
            {/* Selector de idioma origen con opción de añadir */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Source language of the text *
                </Typography>
                <Button
                  size="small"
                  startIcon={<LanguageIcon />}
                  onClick={() => setShowAddLanguageDialog(true)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Add language
                </Button>
              </Box>
              
              <FormControl fullWidth>
                <InputLabel>Select source language</InputLabel>
                <Select
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  label="Select source language"
                  disabled={loadingLanguages}
                >
                  {availableLanguages
                    .filter(lang => lang.name !== 'English')
                    .map((lang) => (
                      <MenuItem key={lang.code} value={lang.name}>
                        {lang.name} ({lang.name_native})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Select the language of the <strong>original text</strong> you are going to upload for translation. The translation will be done from this language to English.
            </Alert>
            
            {/* Idioma de destino fijo a English */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Target Language
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', border: 1, borderColor: 'grey.200' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label="English" 
                    color="success" 
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    (Fixed - Not editable)
                  </Typography>
                </Box>
              </Paper>
            </Box>
            
            <Alert severity="success" sx={{ mb: 2 }}>
              Translation will be done from <strong>{formData.name}</strong> to <strong>English</strong>
            </Alert>
            
            <TextField
              fullWidth
              label="Domain"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., Construction, Electrical Engineering"
            />
            
            <TextField
              fullWidth
              label="Translation Context"
              value={formData.context}
              onChange={(e) => setFormData({ ...formData, context: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
              placeholder="Additional context to improve translation. e.g., 'Technical translation of electrical construction requirements for infrastructure projects'"
              helperText="This context will be added to the translation prompt to improve quality"
            />
            
            {/* Selector de glosarios */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Active Glossaries (Optional)
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select glossaries to use</InputLabel>
                <Select
                  multiple
                  value={formData.glossary_ids}
                  onChange={(e) => setFormData({ ...formData, glossary_ids: e.target.value })}
                  label="Select glossaries to use"
                  disabled={loadingGlossaries}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const glossary = availableGlossaries.find(g => g.id === id);
                        return glossary ? (
                          <Chip key={id} label={glossary.name} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {availableGlossaries.map((glossary) => (
                    <MenuItem key={glossary.id} value={glossary.id}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.glossary_ids.includes(glossary.id)}
                            size="small"
                          />
                        }
                        label={glossary.name}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ mt: 1 }}>
                Select which glossaries should be used for translating terms in this configuration. Only active glossaries with translations will improve the quality of the translation.
              </Alert>
            </Box>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Use as default configuration"
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          variant="text"
          sx={{ 
            color: '#666',
            '&:hover': { 
              bgcolor: 'rgba(0, 0, 0, 0.04)' 
            }
          }}
        >
          Close
        </Button>
        {showForm && (
          <Button 
            variant="contained"
            onClick={editingConfig ? handleUpdateConfig : handleCreateConfig}
            disabled={!formData.name || !formData.target_language}
            sx={{ 
              bgcolor: '#000',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': { 
                bgcolor: '#212121',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
              },
              '&:disabled': {
                bgcolor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
      
      {/* Dialog para añadir nuevo idioma */}
      <Dialog 
        open={showAddLanguageDialog} 
        onClose={() => {
          setShowAddLanguageDialog(false);
          setNewLanguageForm({ name: '', name_native: '' });
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LanguageIcon />
            Add New Language
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name in English"
              value={newLanguageForm.name}
              onChange={(e) => setNewLanguageForm({ ...newLanguageForm, name: e.target.value })}
              fullWidth
              required
              helperText="e.g., Dutch, Spanish, German"
              autoFocus
            />
            <TextField
              label="Native Name"
              value={newLanguageForm.name_native}
              onChange={(e) => setNewLanguageForm({ ...newLanguageForm, name_native: e.target.value })}
              fullWidth
              required
              helperText="e.g., Nederlands, Español, Deutsch"
            />
            <Alert severity="info">
              The language code will be generated automatically.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowAddLanguageDialog(false);
              setNewLanguageForm({ name: '', name_native: '' });
            }}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateLanguage}
            variant="contained"
            disabled={!newLanguageForm.name || !newLanguageForm.name_native}
            sx={{ 
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' }
            }}
          >
            Create Language
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TranslationConfigManager;
